import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const UserContext = createContext(null);

export const useUser = () => {
    const context = useContext(UserContext);
    if (!context) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isAuthenticated, setIsAuthenticated] = useState(false);

    useEffect(() => {
        checkAuth();
    }, []);

    const checkAuth = async () => {
        const token = localStorage.getItem('userToken');
        if (!token) {
            setLoading(false);
            return;
        }

        // Set token in headers for the check
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        try {
            const response = await api.get('/user/me');
            setUser(response.data.user);
            setIsAuthenticated(true);
        } catch (error) {
            console.error('User auth check failed:', error);
            localStorage.removeItem('userToken');
            delete api.defaults.headers.common['Authorization'];
        } finally {
            setLoading(false);
        }
    };

    const login = async (email, password) => {
        const response = await api.post('/user/auth/login', { email, password });
        const { token, user: userData } = response.data;

        localStorage.setItem('userToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setIsAuthenticated(true);

        return userData;
    };

    const register = async (name, email, password, mobile) => {
        const response = await api.post('/user/auth/register', { name, email, password, mobile });
        const { token, user: userData } = response.data;

        localStorage.setItem('userToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setIsAuthenticated(true);

        return userData;
    };

    const googleLogin = async (credential) => {
        const response = await api.post('/user/auth/google', { credential });
        const { token, user: userData } = response.data;

        localStorage.setItem('userToken', token);
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        setUser(userData);
        setIsAuthenticated(true);

        return userData;
    };

    const logout = () => {
        localStorage.removeItem('userToken');
        delete api.defaults.headers.common['Authorization'];
        setUser(null);
        setIsAuthenticated(false);
    };

    const value = {
        user,
        loading,
        isAuthenticated,
        login,
        register,
        googleLogin,
        logout,
        checkAuth
    };

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export default UserContext;
